"use client";

import { c as _c } from "react/compiler-runtime";
import ProfileAvatar, { ProfileBadgeSize } from "@/components/common/profile/ProfileAvatar";
import MediaDisplay from "@/components/drops/view/item/content/media/MediaDisplay";
import { formatNumberWithCommas } from "@/helpers/Helpers";
import { ImageScale } from "@/helpers/image.helpers";
import { getDropPreviewImageUrl, type ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import Link from "next/link";
import { useMemo } from "react";
interface LeadingCardProps {
  readonly drop: ExtendedDrop;
  readonly rank: number;
}
const getRankLabelClass = (rank: number) => {
  if (rank === 1) return "tw-text-[#E8D48A]";
  if (rank === 2) return "tw-text-[#DDDDDD]";
  if (rank === 3) return "tw-text-[#CD7F32]";
  return "tw-text-white/60";
};
export const LeadingCard = t0 => {
  const $ = _c(43);
  const {
    drop,
    rank
  } = t0;
  const media = drop.parts[0]?.media[0];
  const isTabletOrSmaller = useMediaQuery("(max-width: 1023px)");
  const mediaImageScale = isTabletOrSmaller ? ImageScale.AUTOx450 : ImageScale.AUTOx600;
  let t1;
  if ($[0] !== drop.metadata) {
    t1 = getDropPreviewImageUrl(drop.metadata);
    $[0] = drop.metadata;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const previewImageUrl = t1;
  const title = drop.title ?? drop.metadata.find(_temp)?.data_value ?? "Untitled";
  const author = drop.author;
  let t2;
  if ($[2] !== rank) {
    t2 = getRankLabelClass(rank);
    $[2] = rank;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const rankLabelClass = t2;
  const t3 = `tw-text-[11px] tw-font-semibold tw-uppercase tw-leading-5 tw-tracking-wide ${rankLabelClass}`;
  const t4 = rank === 1 ? "LEADING" : `CURRENT TOP ${rank}`;
  let t5;
  if ($[4] !== t3 || $[5] !== t4) {
    t5 = <span className={t3}>{t4}</span>;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== drop.rating) {
    t6 = formatNumberWithCommas(drop.rating);
    $[7] = drop.rating;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t6) {
    t7 = <span className="tw-text-white/80">{t6}</span>;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <span className="tw-text-white/50"> TDH</span>;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t7) {
    t9 = <span className="tw-font-mono tw-text-xs">{t7}{t8}</span>;
    $[12] = t7;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t5 || $[15] !== t9) {
    t10 = <div className="tw-flex tw-flex-col tw-items-start tw-gap-1 tw-border-b tw-border-white/10 tw-bg-iron-900 tw-px-2.5 tw-py-1.5 @sm:tw-flex-row @sm:tw-items-center @sm:tw-justify-between sm:tw-px-3 sm:tw-py-2">{t5}{t9}</div>;
    $[14] = t5;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== drop.parts[0]?.media || $[18] !== media || $[19] !== mediaImageScale || $[20] !== previewImageUrl) {
    t11 = <div className="tw-relative tw-flex tw-aspect-[3/4] tw-max-h-[clamp(320px,70vw,500px)] tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-bg-iron-950 tw-p-2 sm:tw-p-3"><div className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-transition-transform tw-duration-700 tw-ease-out group-hover:tw-scale-105">{media ? <MediaDisplay media_mime_type={drop.parts[0]?.media[0].mime_type ?? "image/jpeg"} media_url={drop.parts[0]?.media[0].url} disableMediaInteraction={true} imageScale={mediaImageScale} previewImageUrl={previewImageUrl} /> : <div className="tw-flex tw-size-full tw-items-center tw-justify-center tw-bg-iron-950"><span className="tw-text-sm tw-text-white/40">No image</span></div>}</div></div>;
    $[17] = drop.parts[0]?.media;
    $[18] = media;
    $[19] = mediaImageScale;
    $[20] = previewImageUrl;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11) {
    t12 = <div className="tw-flex tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-iron-950 tw-transition-colors group-hover:tw-border-white/10">{t10}{t11}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const t13 = `/waves?wave=${drop.wave.id}&drop=${drop.id}`;
  let t14;
  if ($[25] !== t13 || $[26] !== title) {
    t14 = <Link href={t13} className="tw-m-0 tw-line-clamp-2 tw-text-base tw-font-semibold tw-leading-tight tw-text-iron-200 tw-no-underline tw-transition-colors group-hover:tw-text-white @lg:tw-line-clamp-1">{title}</Link>;
    $[25] = t13;
    $[26] = title;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const t15 = `/${author.handle ?? author.primary_address}`;
  const t16 = author.handle ?? "Artist";
  let t17;
  if ($[28] !== author.pfp || $[29] !== t16) {
    t17 = <ProfileAvatar pfpUrl={author.pfp} alt={t16} size={ProfileBadgeSize.SMALL} />;
    $[28] = author.pfp;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  const t18 = author.handle ?? "Anonymous";
  let t19;
  if ($[31] !== t18) {
    t19 = <span className="tw-min-w-0 tw-truncate tw-text-xs tw-text-white/50 desktop-hover:hover:tw-text-white">{t18}</span>;
    $[31] = t18;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t15 || $[34] !== t17 || $[35] !== t19) {
    t20 = <Link href={t15} className="tw-mt-2 tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-no-underline">{t17}{t19}</Link>;
    $[33] = t15;
    $[34] = t17;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t14 || $[38] !== t20) {
    t21 = <div className="tw-flex tw-flex-col tw-gap-3"><div>{t14}{t20}</div></div>;
    $[37] = t14;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t12 || $[41] !== t21) {
    t22 = <div className="tw-group tw-flex tw-flex-col tw-gap-3 tw-text-left tw-transition-all tw-duration-500 desktop-hover:tw-opacity-70 desktop-hover:tw-grayscale desktop-hover:hover:tw-opacity-100 desktop-hover:hover:tw-grayscale-0 sm:tw-gap-4">{t12}{t21}</div>;
    $[40] = t12;
    $[41] = t21;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  return t22;
};
function _temp(m) {
  return m.data_key === "title";
}